import type {
  TextBlock,
  TextDocument,
  TextRun,
  TextSelection,
  TextSelectionPosition,
} from '../model/textDocument';

const LINE_CONTENT_SELECTOR = '[data-text-line-content]';
const EDIT_BLOCK_SELECTOR = '[data-text-edit-block]';
const RUN_SELECTOR = '[data-text-run-id]';
const ZERO_WIDTH_SPACE = '\u200B';

function textLength(text: string): number {
  return text.split(ZERO_WIDTH_SPACE).join('').length;
}

function firstPositionInBlock(block: TextBlock): TextSelectionPosition {
  const firstRun = block.runs[0];
  return {
    blockId: block.id,
    runId: firstRun?.id ?? '',
    offset: 0,
  };
}

export function firstTextSelection(document: TextDocument): TextSelection | null {
  const block = document.blocks[0];
  if (!block) return null;
  const position = firstPositionInBlock(block);
  return { anchor: position, focus: position };
}

export function endPositionInBlock(block: TextBlock): TextSelectionPosition {
  const lastRun = block.runs[block.runs.length - 1];
  return {
    blockId: block.id,
    runId: lastRun?.id ?? '',
    offset: lastRun ? lastRun.text.length : 0,
  };
}

export function blockPlainTextLength(block: TextBlock): number {
  return block.runs.reduce((sum, run) => sum + run.text.length, 0);
}

export function flatOffsetInBlock(block: TextBlock, position: TextSelectionPosition): number {
  if (!position.runId) {
    return Math.min(position.offset, blockPlainTextLength(block));
  }
  let offset = 0;
  for (const run of block.runs) {
    if (run.id === position.runId) {
      return offset + Math.min(position.offset, run.text.length);
    }
    offset += run.text.length;
  }
  return offset;
}

export function positionFromFlatOffset(block: TextBlock, flatOffset: number): TextSelectionPosition {
  let remaining = Math.max(0, flatOffset);
  for (const run of block.runs) {
    if (remaining <= run.text.length) {
      return { blockId: block.id, runId: run.id, offset: remaining };
    }
    remaining -= run.text.length;
  }
  const lastRun = block.runs[block.runs.length - 1];
  if (!lastRun) return { blockId: block.id, runId: '', offset: 0 };
  return { blockId: block.id, runId: lastRun.id, offset: lastRun.text.length };
}

export function selectionIsCollapsed(selection: TextSelection | null): boolean {
  if (!selection) return true;
  return selection.anchor.blockId === selection.focus.blockId
    && selection.anchor.runId === selection.focus.runId
    && selection.anchor.offset === selection.focus.offset;
}

export function remapSelection(document: TextDocument, selection: TextSelection): TextSelection {
  const anchorBlock = document.blocks.find((block) => block.id === selection.anchor.blockId);
  const focusBlock = document.blocks.find((block) => block.id === selection.focus.blockId);
  if (!anchorBlock || !focusBlock) return selection;
  return {
    anchor: positionFromFlatOffset(anchorBlock, flatOffsetInBlock(anchorBlock, selection.anchor)),
    focus: positionFromFlatOffset(focusBlock, flatOffsetInBlock(focusBlock, selection.focus)),
  };
}

function blockElementForNode(node: Node | null): HTMLElement | null {
  if (!node) return null;
  const element = node.nodeType === Node.ELEMENT_NODE ? node as Element : node.parentElement;
  return element?.closest<HTMLElement>(EDIT_BLOCK_SELECTOR) ?? null;
}

function offsetWithinRun(runElement: HTMLElement, targetNode: Node, targetOffset: number): number {
  let offset = 0;
  const walker = document.createTreeWalker(runElement, NodeFilter.SHOW_TEXT);
  while (walker.nextNode()) {
    const textNode = walker.currentNode;
    if (textNode === targetNode) {
      return offset + targetOffset;
    }
    offset += textLength(textNode.textContent ?? '');
  }
  return offset;
}

function positionFromDomPoint(root: HTMLElement, node: Node | null, domOffset: number): TextSelectionPosition | null {
  const blockElement = blockElementForNode(node);
  if (!blockElement || !root.contains(blockElement)) return null;

  const blockId = blockElement.dataset.textBlockId;
  if (!blockId) return null;

  const pointElement = node?.nodeType === Node.ELEMENT_NODE ? node as Element : node?.parentElement;
  const runElement = pointElement?.closest<HTMLElement>(RUN_SELECTOR);
  if (runElement?.dataset.textRunId) {
    return {
      blockId,
      runId: runElement.dataset.textRunId,
      offset: offsetWithinRun(runElement, node!, domOffset),
    };
  }

  const lineContent = blockElement.querySelector<HTMLElement>(LINE_CONTENT_SELECTOR);

  if (lineContent) {
    let flatOffset = 0;
    const walker = document.createTreeWalker(lineContent, NodeFilter.SHOW_TEXT);
    while (walker.nextNode()) {
      const textNode = walker.currentNode;
      if (textNode === node) {
        flatOffset += textLength((textNode.textContent ?? '').slice(0, domOffset));
        return { blockId, runId: '', offset: flatOffset };
      }
      if ((textNode.parentElement as Element | null)?.closest('[data-placeholder], [data-list-marker]')) {
        continue;
      }
      flatOffset += textLength(textNode.textContent ?? '');
    }

    if (node?.nodeType === Node.ELEMENT_NODE) {
      const el = node as Element;
      let idx = 0;
      let elOffset = 0;
      for (const child of Array.from(el.childNodes)) {
        if (idx >= domOffset) break;
        idx++;
        if ((child as Element).matches?.('[data-placeholder], [data-list-marker]')) continue;
        elOffset += textLength(child.textContent ?? '');
      }
      return { blockId, runId: '', offset: elOffset };
    }
  }

  if (node?.nodeType === Node.TEXT_NODE) {
    let flatOffset = 0;
    const outerWalker = document.createTreeWalker(blockElement, NodeFilter.SHOW_TEXT);
    while (outerWalker.nextNode()) {
      const textNode = outerWalker.currentNode;
      if (textNode === node) {
        flatOffset += textLength((textNode.textContent ?? '').slice(0, domOffset));
        return { blockId, runId: '', offset: flatOffset };
      }
      if ((textNode.parentElement as Element | null)?.closest('[data-placeholder], [data-list-marker]')) {
        continue;
      }
      flatOffset += textLength(textNode.textContent ?? '');
    }
  }

  return null;
}

/**
 * Compute the flat text offset of a DOM cursor position (node + domOffset) within a
 * block element's line-content div. Uses explicit tree-walking instead of Range.toString()
 * so that display:none placeholder and list-marker nodes are always excluded.
 */
export function flatOffsetInBlockElement(
  blockElement: HTMLElement,
  node: Node | null,
  domOffset: number,
): number {
  const lineContent = blockElement.querySelector<HTMLElement>(LINE_CONTENT_SELECTOR);
  if (!lineContent || !node) return 0;

  /** Recursively sum visible text length, skipping placeholder / list-marker subtrees. */
  function subtreeLen(root: Node): number {
    if (root.nodeType === Node.TEXT_NODE) return textLength(root.textContent ?? '');
    if (root.nodeType !== Node.ELEMENT_NODE) return 0;
    if ((root as Element).matches('[data-placeholder], [data-list-marker]')) return 0;
    let total = 0;
    root.childNodes.forEach((c) => { total += subtreeLen(c); });
    return total;
  }

  // ── Case A: cursor is inside a text node ──────────────────────────────────
  if (node.nodeType === Node.TEXT_NODE) {
    if (!lineContent.contains(node)) return 0;
    // Cursor inside placeholder/marker — treat as offset 0
    if ((node.parentElement as Element | null)?.closest('[data-placeholder], [data-list-marker]')) {
      return 0;
    }
    let offset = 0;
    const walker = document.createTreeWalker(lineContent, NodeFilter.SHOW_TEXT);
    while (walker.nextNode()) {
      const tn = walker.currentNode as Text;
      const skipped = (tn.parentElement as Element | null)
        ?.closest('[data-placeholder], [data-list-marker]');
      if (tn === node) {
        if (!skipped) offset += textLength((tn.textContent ?? '').slice(0, domOffset));
        return offset;
      }
      if (!skipped) offset += textLength(tn.textContent ?? '');
    }
    return offset;
  }

  // ── Case B: cursor is at element level ───────────────────────────────────
  if (node.nodeType !== Node.ELEMENT_NODE) return 0;

  let container: Node;
  let childIdx: number;

  if (node === lineContent || lineContent.contains(node as Element)) {
    // Cursor between children of lineContent (or a descendant element).
    container = node;
    childIdx = domOffset;
  } else if (node === blockElement) {
    // Cursor between children of blockElement (e.g. marker | lineContent).
    const kids = Array.from(blockElement.childNodes);
    const lcIdx = kids.indexOf(lineContent);
    if (lcIdx < 0 || domOffset <= lcIdx) return 0;
    // Cursor is at or after lineContent → treat as end of content.
    container = lineContent;
    childIdx = lineContent.childNodes.length;
  } else {
    return 0;
  }

  let offset = 0;
  let i = 0;
  for (const child of Array.from(container.childNodes)) {
    if (i >= childIdx) break;
    i++;
    offset += subtreeLen(child);
  }
  return offset;
}

export function selectionWithFlatOffsets(
  document: TextDocument,
  selection: TextSelection,
  root: HTMLElement,
): TextSelection {
  const winSel = window.getSelection();
  const toFlat = (
    pos: TextSelectionPosition,
    point: { node: Node | null; offset: number },
  ): TextSelectionPosition => {
    const blockEl = root.querySelector<HTMLElement>(
      `${EDIT_BLOCK_SELECTOR}[data-text-block-id="${CSS.escape(pos.blockId)}"]`,
    );
    if (blockEl && point.node) {
      return {
        blockId: pos.blockId,
        runId: '',
        offset: flatOffsetInBlockElement(blockEl, point.node, point.offset),
      };
    }
    const block = document.blocks.find((b) => b.id === pos.blockId);
    if (!block) return { ...pos, runId: '', offset: 0 };
    return { blockId: pos.blockId, runId: '', offset: flatOffsetInBlock(block, pos) };
  };
  return {
    anchor: toFlat(selection.anchor, {
      node: winSel?.anchorNode ?? null,
      offset: winSel?.anchorOffset ?? 0,
    }),
    focus: toFlat(selection.focus, {
      node: winSel?.focusNode ?? null,
      offset: winSel?.focusOffset ?? 0,
    }),
  };
}

export function readTextSelectionFromDom(root: HTMLElement): TextSelection | null {
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) return null;
  const anchor = positionFromDomPoint(root, selection.anchorNode, selection.anchorOffset);
  const focus = positionFromDomPoint(root, selection.focusNode, selection.focusOffset);
  if (!anchor || !focus) return null;
  return { anchor, focus };
}

function findBlockElement(root: HTMLElement, blockId: string): HTMLElement | null {
  return root.querySelector<HTMLElement>(`${EDIT_BLOCK_SELECTOR}[data-text-block-id="${CSS.escape(blockId)}"]`);
}

function findRunElement(blockElement: HTMLElement, runId: string): HTMLElement | null {
  if (!runId) return null;
  return blockElement.querySelector<HTMLElement>(`${RUN_SELECTOR}[data-text-run-id="${CSS.escape(runId)}"]`);
}

function domPointForPosition(root: HTMLElement, position: TextSelectionPosition): { node: Node; offset: number } | null {
  const blockElement = findBlockElement(root, position.blockId);
  if (!blockElement) return null;
  const runElement = findRunElement(blockElement, position.runId);
  const container = runElement ?? blockElement.querySelector<HTMLElement>(LINE_CONTENT_SELECTOR);
  if (!container) return null;

  let remaining = position.offset;
  const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
  while (walker.nextNode()) {
    const textNode = walker.currentNode;
    if ((textNode.parentElement as Element | null)?.closest('[data-placeholder], [data-list-marker]')) {
      continue;
    }
    const text = textNode.textContent ?? '';
    const len = textLength(text);
    if (remaining <= len) {
      return { node: textNode, offset: Math.min(remaining, text.length) };
    }
    remaining -= len;
  }

  const seed = document.createTextNode(ZERO_WIDTH_SPACE);
  container.appendChild(seed);
  return { node: seed, offset: 0 };
}

export function restoreTextSelectionToDom(root: HTMLElement, selection: TextSelection | null): void {
  if (!selection) return;
  const anchor = domPointForPosition(root, selection.anchor);
  const focus = domPointForPosition(root, selection.focus);
  const domSelection = window.getSelection();
  if (!anchor || !focus || !domSelection) return;
  const range = document.createRange();
  range.setStart(anchor.node, anchor.offset);
  range.setEnd(focus.node, focus.offset);
  domSelection.removeAllRanges();
  domSelection.addRange(range);
}
